import { ChevronRight } from 'lucide-react';
import { ChecklistItemToggleRow, TINT_BORDER } from './ChecklistItemToggleRow';
import {
  SWITCH_DESCRIPTION,
  SWITCH_LABEL,
  leadRowSubtitle,
  leadRowTitle,
  manageButtonLabel,
} from '@/lib/exampleAuthoringCopy';

interface ExamplePhotosSwitchRowProps {
  /** Suffix so the add form and the edit form get distinct element ids. */
  idSuffix: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  count: number;
  /** Up to three thumbnail urls for the lead row. */
  previews: string[];
  onManage: () => void;
}

/**
 * EXP-2.1 · The Example photos switch, and the row it reveals.
 *
 * The switch itself is `ChecklistItemToggleRow`, the same component Required
 * and Allow photo uploads use, so all three read as one column of switches
 * with the control on the left. This adds the row underneath.
 *
 * ⚠️ That sameness is load-bearing. When this control was a card with its
 * checkbox floated to the right, an admin turned photo attachments on, saw the
 * words "Example photos" appear, and could not find the switch for it.
 */
export function ExamplePhotosSwitchRow({
  idSuffix,
  enabled,
  onEnabledChange,
  count,
  previews,
  onManage,
}: ExamplePhotosSwitchRowProps) {
  return (
    <ChecklistItemToggleRow
      id={`examples_enabled_${idSuffix}`}
      checked={enabled}
      onCheckedChange={onEnabledChange}
      title={SWITCH_LABEL}
      description={SWITCH_DESCRIPTION}
      lit
    >
      {/* ⚠️ Off REMOVES this from the DOM rather than disabling it. A disabled
          row on every item that does not use the feature is permanent furniture
          advertising something most items do not have. Switching off keeps the
          photos, so switching back on returns them. */}
      <div
        className="flex items-center justify-between gap-3 rounded-b-lg border border-t-0 bg-primaryLight px-3 py-2.5"
        style={{ borderColor: TINT_BORDER }}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          {previews.length > 0 && (
            <span aria-hidden="true" className="inline-flex flex-none">
              {previews.slice(0, 3).map((url, n) => (
                <span
                  key={url}
                  className={[
                    'block h-7 w-7 overflow-hidden rounded border-[1.5px] border-white',
                    n > 0 ? '-ml-2' : '',
                  ].join(' ')}
                >
                  <img src={url} alt="" className="h-full w-full object-cover" />
                </span>
              ))}
            </span>
          )}
          <span className="min-w-0">
            <span className="block text-[11.5px] font-semibold text-primaryHover">
              {leadRowTitle(count)}
            </span>
            <span className="block text-[11px] text-muted-foreground">
              {leadRowSubtitle(count)}
            </span>
          </span>
        </div>

        <button
          type="button"
          onClick={onManage}
          className="inline-flex flex-none items-center gap-1 whitespace-nowrap rounded-md border border-border bg-background px-2.5 py-1 text-[11.5px] font-semibold text-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {manageButtonLabel(count)}
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    </ChecklistItemToggleRow>
  );
}
